// Under the timeline: nothing with nothing selected (the add buttons live in the episode header); with a clip selected, its name, time and actions.
import { useLibrary } from "../../library/store";
import { findAsset } from "../../model/library";
import { lineById, markById, type TimelineEpisode } from "../../model/timeline";
import { Avatar, Icon } from "../common";
import type { Sel } from "./Lanes";
import { fmtT } from "./Stage";

export type Act = "edit" | "faces" | "animate" | "earlier" | "later" | "delete";

// What is selected, found in the current episode (null if it has gone, e.g. after undo).
export function findSel(ep: TimelineEpisode, s: Sel | null): { start: number; end: number; silent: boolean } | null {
  if (!s) return null;
  if (s.kind === "line") { const b = lineById(ep, s.id); return b ? { start: b.start, end: b.end, silent: b.how === "silent" } : null; }
  const m = markById(ep, s.kind, s.id);
  return m ? { start: m.t, end: m.end, silent: false } : null;
}

export function Toolbar({ ep, sel, onAct, onDeselect }: {
  ep: TimelineEpisode; sel: Sel | null; onAct: (a: Act) => void; onDeselect: () => void;
}) {
  const lib = useLibrary();
  const item = findSel(ep, sel);
  if (!sel || !item) return null;
  const who = sel.char ? findAsset(lib, sel.char)?.name ?? "" : "";
  const name = sel.kind === "camera" ? "Camera" : sel.kind === "picture" ? "Picture" : sel.kind === "feeling" ? `${who}'s expression` : item.silent ? "Silence" : who || "Line";
  const index = sel.kind === "line" ? ep.lines.findIndex(b => b.id === sel.id) : -1;
  const B = (a: Act, label: string, icon: string, o: { off?: boolean; danger?: boolean; first?: boolean } = {}) => (
    <button key={a} className={`tl-act${o.first ? " first" : ""}${o.danger ? " danger" : ""}`} disabled={o.off} onClick={() => onAct(a)}><Icon name={icon} size={20} />{label}</button>
  );
  const many = sel.kind === "picture" || sel.kind === "line";
  return (
    <div className="tl-toolbar">
      <div className="tl-tb-head">
        {sel.kind === "line" && sel.char && !item.silent && <Avatar assetId={sel.char} size={36} />}
        <span className="tl-tb-name"><b>{name}</b><span className="muted">{fmtT(item.start)} – {fmtT(item.end)}</span></span>
        <button className="icon-btn" onClick={onDeselect} aria-label="Deselect"><Icon name="close" size={20} /></button>
      </div>
      <div className={`tl-tb-acts${many ? "" : " few"}`}>
        {sel.kind === "line" && <>
          {B("edit", "Open", "edit", { first: true })}
          {B("earlier", "Earlier", "back", { off: index <= 0 })}
          {B("later", "Later", "chevron", { off: index < 0 || index >= ep.lines.length - 1 })}
        </>}
        {sel.kind === "picture" && <>{B("edit", "Edit picture", "edit", { first: true })}{B("faces", "Faces", "person")}{B("animate", "Animate", "film")}</>}
        {(sel.kind === "camera" || sel.kind === "feeling") && B("edit", "Edit", "edit", { first: true })}
        {B("delete", "Delete", "trash", { danger: true })}
      </div>
    </div>
  );
}
